// DataView.js
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BarChart3 } from 'lucide-react';
import { api } from "../../api";

import Header from '../../components/header/header';
import ViewToggle from '../../components/toggles/ViewToggle/ViewToggle';
import ChartControls from '../../components/ChartControls/ChartControls';
import { getChartOptions } from '../../components/ChartOptions/ChartOptions';
import LoadingProvider from '../../components/LoadingProvider/LoadingProvider';
import ChartSection from '../../components/business/ChartSection/ChartSection';
import QRScanDisplay from '../../components/QRScansDisplay/QRScansDisplay';
import ComplaintsPieChart from '../../components/ComplaintFeedback/ComplaintFeedback';
import SmartWordCloudLoader from './WordCloudSpinner';
import BusinessMetrics from '../../components/business/BusinessMetrics/BusinessMetrics';
import { calculateSiteHealthScore } from '../../components/SiteHealthScore/SiteHealthScore';
import SiteHealthScoreCard from '../../components/SiteHealthScoreCard/SiteHealthScoreCard';
import {
  useBusinessData,
  useStoreData,
  useDeckData,
  useChartData,
  useDashboardMetrics,
  useAnalytics,
  useSiteHealthScore,
  useDevicesList,
  useDecksList
} from '../../components/hooks/UseDataHooks';

import { useSiteAvailability } from '../../components/hooks/useSiteAvailability';
import { useLoadingCoordinator } from '../../components/hooks/UseLoadingCoordinator';
import usePollingAvailability from '../../hooks/usePollingAvailability';
import { 
  fetchBusinessByEmail,
  fetchStoresByBusinessId, 
  fetchLinesByToken, 
  fetchLinesLastWeek, 
  fetchBusinessViewDataByToken, 
  getIndustryByToken
} from './BusinessUtilities';

import './DataView.css';

import posthog from '../../components/PostHog/posthog';
import UnifiedDropdown from '../../components/toggles/Dropdown/Dropdown';
import Button from '../../components/Button/Button';

// Safe localStorage access functions
const getUserFromStorage = () => {
  try {
    return localStorage.getItem('user');
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return null;
  }
};

const getSelectedBusinessIdFromStorage = () => {
  try {
    return localStorage.getItem('selectedBusinessId');
  } catch (error) {
    console.error('Error accessing localStorage for selectedBusinessId:', error);
    return null;
  }
};

const setSelectedBusinessIdToStorage = (businessId) => {
  try {
    if (businessId) {
      localStorage.setItem('selectedBusinessId', businessId);
    } else {
      localStorage.removeItem('selectedBusinessId');
    }
  } catch (error) {
    console.error('Error setting localStorage for selectedBusinessId:', error);
  }
};

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  return isMobile;
};

const USER = getUserFromStorage();

// Modern styling constants
const MODERN_STYLES = {
  modernContainer: {
    background: '#F8FAFC',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    border: '2px solid #e2e8f0',
    // boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
    height: '100%'
  },
  healthScoreContainer: {
    background: '#F8FAFC',
    backdropFilter: 'blur(20px)',
    border: '2px solid #e2e8f0',
    maxWidth: '600px'
  }
};


// Business View Component
const BusinessViewContent = ({ 
  sumOfSites, 
  totalLines, 
  lastWeekLines, 
  businessTimeSeriesData, 
  isBusinessDataLoading, 
  hasEnoughDataForCharts, 
  getOption,
  selectedBusinessId 
}) => {
  const isMobile = useMobileDetection();
  
  // Your existing state and logic remains the same
  const [isStackableView, setIsStackableView] = useState(true);
  const [stackableData, setStackableData] = useState(null);
  const [stackableLoading, setStackableLoading] = useState(false);
  const [stackableError, setStackableError] = useState(null);
  
  // Dynamic row heights
  const firstRowHeight = '20%';
  const lastRowHeight = '70%';

  // Check if we have enough time series data for the alternate view
  const hasTimeSeriesData = hasEnoughDataForCharts && 
    businessTimeSeriesData && 
    businessTimeSeriesData.length > 0;

  // Function to fetch stackable data
  const fetchStackableData = useCallback(async () => {
    console.log('fetchStackableData called with selectedBusinessId:', selectedBusinessId);
    if (!selectedBusinessId) {
      console.log('No selectedBusinessId, returning early');
      return;
    }
    
    setStackableLoading(true);
    setStackableError(null);
    
    try {
      // Calculate date range - last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      const startDateStr = startDate.toISOString().slice(0, 19).replace('T', ' ');
      const endDateStr = endDate.toISOString().slice(0, 19).replace('T', ' ');
      
      console.log('Fetching stackable data for business:', selectedBusinessId, 'dates:', startDateStr, 'to', endDateStr);
      const response = await api.get(`/analytics/stackable-chart/${selectedBusinessId}?startDate=${startDateStr}&endDate=${endDateStr}`);
      
      console.log('Stackable data response:', response.data);
      setStackableData(response.data);
    } catch (error) {
      console.error('Error fetching stackable chart data:', error);
      setStackableError(error.response?.data?.error || 'Failed to load chart data');
    } finally {
      setStackableLoading(false);
    }
  }, [selectedBusinessId]);

  // Fetch stackable data when component mounts or selectedBusinessId changes
  useEffect(() => {
    console.log('useEffect triggered - selectedBusinessId:', selectedBusinessId);
    if (selectedBusinessId) {
      console.log('Calling fetchStackableData');
      fetchStackableData();
    }
  }, [selectedBusinessId, fetchStackableData]);

  // Toggle between chart types - only allow if time series data exists
  const handleChartTypeToggle = (showStackable) => {
    console.log('Toggle clicked:', showStackable, 'hasTimeSeriesData:', hasTimeSeriesData);
    
    // If trying to switch to time series view but no data exists, stay on stackable
    if (!showStackable && !hasTimeSeriesData) {
      console.log('Cannot switch to time series view - no data available');
      return;
    }
    
    setIsStackableView(showStackable);
  };

  // Generate business insights with null/undefined safety
  const businessInsights = [
    {
      text: `Managing ${sumOfSites || 0} active sites`,
      priority: 'medium'
    },
    {
      text: `${(totalLines || 0) > (lastWeekLines || 0) ? 'Increased' : 'Decreased'} activity vs last week`,
      priority: (totalLines || 0) > (lastWeekLines || 0) ? 'high' : 'medium'
    },
    {
      text: `${Math.round((((totalLines || 0) - (lastWeekLines || 0)) / ((lastWeekLines || 1))) * 100)}% change in engagement`,
      priority: 'low'
    }
  ].filter(insight => (sumOfSites || 0) > 0);

  // Determine what to show in the chart section
  const getChartContent = () => {
    // If we're in stackable view (default)
    if (isStackableView) {
      // Show stackable chart with appropriate state
      return (
        <ChartSection 
          data={stackableData || []}
          title="Kiosk Performance Distribution"
          chartType="line2"
          isLoading={stackableLoading}
          showInsights={true}
          insights={businessInsights}
          getOption={getOption}
          stackableData={stackableData}
          onChartTypeToggle={handleChartTypeToggle}
          isStackableView={true}
          error={stackableError}
          hasTimeSeriesData={hasTimeSeriesData} // Pass this to control toggle availability
          noDataMessage={stackableError ? stackableError : "No kiosk performance data available"}
        />
      );
    }
    
    // If we're in time series view and have data
    if (hasTimeSeriesData) {
      return (
        <ChartSection 
          data={businessTimeSeriesData}
          title="Site Performance Over Time"
          chartType="line2"
          isLoading={isBusinessDataLoading}
          showInsights={true}
          insights={businessInsights}
          getOption={getOption}
          stackableData={stackableData}
          onChartTypeToggle={handleChartTypeToggle}
          isStackableView={false}
          hasTimeSeriesData={hasTimeSeriesData}
        />
      );
    }
    
    // Fallback - should not reach here due to toggle logic, but just in case
    return (
      <ChartSection
        data={[]}
        title="Site Performance Over Time"
        chartType="line2"
        isLoading={false}
        showInsights={false}
        getOption={getOption}
        noDataMessage="Check back in a week or so for time series data..."
        hasTimeSeriesData={hasTimeSeriesData}
        onChartTypeToggle={handleChartTypeToggle}
        isStackableView={false}
      />
    );
  };

  if (isMobile) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        width: '100%',
        gap: '16px',
        paddingBottom: '20px' // Extra padding for scroll
      }}>
        {/* Business Metrics - Full width on mobile */}
        <div style={{ 
          minHeight: '200px',
          width: '100%',
          flexShrink: 0
        }}>      
          <BusinessMetrics
            sumOfSites={sumOfSites || 0}
            totalLines={totalLines || 0}
            lastWeekLines={lastWeekLines || 0}
          />
        </div>
        
        {/* Chart Section - Full width on mobile */}
        <div style={{ 
          minHeight: '300px',
          width: '100%',
          flexShrink: 0
        }}>
          {getChartContent()}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      overflow: 'auto',
      width: '100%'
    }}>
      {/* First row - Business Metrics */}
      <div style={{ 
        height: firstRowHeight,
        position: 'relative',
        transition: 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}>      
        <BusinessMetrics
          sumOfSites={sumOfSites || 0}
          totalLines={totalLines || 0}
          lastWeekLines={lastWeekLines || 0}
        />
      </div>
      
      {/* Second row - Chart Section */}
      <div style={{ 
        height: lastRowHeight,
        minHeight: '200px',
        display: 'flex',
        width: '100%',
        transition: 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      }}>
        {getChartContent()}
      </div>
    </div>
  );
};

// Site View Co mponent
const SiteViewContent = ({
  selectedStoreId,
  filteredQuestion1Data,
  question1Title,
  isDataLoading,
  filteredData,
  getOption,
  dateRange,
  selectedBusinessId,
  businessId
}) => {
  const isMobile = useMobileDetection();
  
  // Use the new site health score hook with dateRange and businessId
  const {
    score,
    trends,
    isLoading: isHealthScoreLoading,
    error: healthScoreError,
    refetch: refetchHealthScore
  } = useSiteHealthScore(
    selectedStoreId,
    dateRange,
    30000, // refreshInterval
    selectedBusinessId || businessId // Pass businessId for "All Sites" aggregation
  );

  const firstRowHeight = '40%';
  const lastRowHeight = '40%';

  // Create siteHealthData object for the component
  const siteHealthData = {
    score: score,
    trends: trends
  };

  if (isMobile) {
    return (
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        gap: '16px',
        paddingBottom: '20px'
      }}>
        {/* Site Health Score Card - Full width row */}
        <div style={{ 
          minHeight: '200px',
          width: '100%',
          flexShrink: 0,
          ...MODERN_STYLES.modernContainer,
          ...MODERN_STYLES.healthScoreContainer
        }}>
          {healthScoreError ? (
            <div style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '12px',
              color: '#ef4444'
            }}>
              <div style={{ fontSize: '20px' }}>⚠️</div>
              <div style={{ fontSize: '14px', fontWeight: '500' }}>
                Error loading health score
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                {healthScoreError}
              </div>
              <button 
                onClick={refetchHealthScore}
                style={{
                  padding: '6px 12px',
                  background: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          ) : (
            <SiteHealthScoreCard 
              siteId={selectedStoreId} 
              score={siteHealthData.score}
              week={siteHealthData.trends.lastWeek}
              month={siteHealthData.trends.lastMonth}
              isLoading={isHealthScoreLoading}
              dateRange={dateRange}
            />
          )}
        </div>

        {/* Primary Feedback Chart - Full width row */}
        <div style={{ 
          minHeight: '250px',
          width: '100%',
          flexShrink: 0
        }}>
          <ChartSection
            data={filteredQuestion1Data}
            title={question1Title}
            chartType="bar"
            isArchived={false}
            isLoading={isDataLoading}
            showInsights={true}
            insights={[{ text: "Primary feedback metric", priority: "medium" }]}
            getOption={getOption}
          />
        </div>
        
        {/* Happy vs Unhappy Chart - Full width row */}
        <div style={{ 
          minHeight: '250px',
          width: '100%',
          flexShrink: 0
        }}>
          <ChartSection
            data={filteredData}
            title={"Happy response vs unhappy response"}
            chartType="line"
            isArchived={false}
            isLoading={isDataLoading}
            showInsights={true}
            insights={[{
              text: "Performance metric showing differentiation in happy and unhappy responses",
              priority: "high"
            }]}
            getOption={getOption}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="site-content" style={{ 
      flex: 1, 
      overflow: 'hidden', 
      display: 'flex', 
      flexDirection: 'column',
      height: 'calc(100vh - 120px)',
      width: '100%'
    }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        gap: '24px',
        overflow: 'hidden',
        padding: '0 0 24px 0',
        width: '100%'
      }}>
        <div className="site-first-row" style={{ 
          display: 'flex', 
          gap: '24px', 
          height: firstRowHeight,
          minHeight: '200px',
          width: '100%',
          transition: 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}>
          <div style={{ 
            flex: 1,
            height: '100%',
            overflow: 'hidden',
            ...MODERN_STYLES.modernContainer,
            ...MODERN_STYLES.healthScoreContainer
          }}>
            {/* Handle error state for health score */}
            {healthScoreError ? (
              <div style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '12px',
                color: '#ef4444'
              }}>
                <div style={{ fontSize: '20px' }}>⚠️</div>
                <div style={{ fontSize: '14px', fontWeight: '500' }}>
                  Error loading health score
                </div>
                <div style={{ fontSize: '12px', color: '#64748b' }}>
                  {healthScoreError}
                </div>
                <button 
                  onClick={refetchHealthScore}
                  style={{
                    padding: '6px 12px',
                    background: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              </div>
            ) : (
              <SiteHealthScoreCard 
                siteId={selectedStoreId} 
                score={siteHealthData.score}
                week={siteHealthData.trends.lastWeek}
                month={siteHealthData.trends.lastMonth}
                isLoading={isHealthScoreLoading}
                dateRange={dateRange} // Pass dateRange to the component
              />
            )}
          </div>
          
          <div style={{ flex: 1, height: '100%' }}>
            <ChartSection
              data={filteredQuestion1Data}
              title={question1Title}
              chartType="bar"
              isArchived={false}
              isLoading={isDataLoading}
              showInsights={true}
              insights={[{ text: "Primary feedback metric", priority: "medium" }]}
              getOption={getOption}
            />
          </div>
        </div>
        
        <div style={{ 
          height: lastRowHeight,
          minHeight: '250px'
        }}>
          <ChartSection
            data={filteredData}
            title={"Happy response vs unhappy response"}
            chartType="line"
            isArchived={false}
            isLoading={isDataLoading}
            showInsights={true}
            insights={[{
              text: "Performance metric showing differentiation in happy and unhappy responses",
              priority: "high"
            }]}
            getOption={getOption}
          />
        </div>
      </div>
    </div>
  );
};

const ResponseViewContent = ({ 
  slideCount, 
  showArchivedGraphs, 
  filteredQuestion1Data, 
  question1Title, 
  isDataLoading, 
  filteredData, 
  getOption,
  selectedStoreId,
  businessId,
  dateRange // Make sure this is in the destructuring
}) => {
  const isMobile = useMobileDetection();
  
  // Use the dashboard metrics hook with dateRange and 30 second refresh
  const effectiveStoreId = selectedStoreId || localStorage.getItem('selectedStoreId');

  const {
    qrMetrics,
    complaintsData,
    combined: { isLoading: metricsLoading, hasError, errorMessage },
    refetchAll
  } = useDashboardMetrics(effectiveStoreId, dateRange, 30000);

  // Debug: Log when QR metrics change
  useEffect(() => {
    console.log('📈 QR Metrics updated:', {
      scans: qrMetrics?.scans,
      conversionRate: qrMetrics?.conversionRate,
      dateRange: qrMetrics?.dateRange,
      isLoading: qrMetrics?.isLoading
    });
  }, [qrMetrics?.scans, qrMetrics?.conversionRate, qrMetrics?.dateRange, qrMetrics?.isLoading]);

  // Debug: Log when complaints data changes
  useEffect(() => {
    console.log('📋 Complaints Data updated:', {
      totalComplaints: complaintsData?.totalComplaints,
      dataLength: complaintsData?.data?.length,
      dateRange: complaintsData?.dateRange,
      isLoading: complaintsData?.isLoading
    });
  }, [complaintsData?.totalComplaints, complaintsData?.data?.length, complaintsData?.dateRange, complaintsData?.isLoading]);

  // Use analytics hook for insights
  const { insights, recommendations, generateInsights } = useAnalytics(effectiveStoreId, businessId);

  // Add a ref to track if insights have been generated for current data
  const lastInsightDataRef = useRef({
    scans: null,
    totalComplaints: null,
    chartDataLength: null,
    dateRange: null
  });

  // Memoize the insight generation trigger to prevent infinite loops
  const shouldGenerateInsights = useMemo(() => {
    if (metricsLoading || !qrMetrics || !complaintsData.data || qrMetrics.scans === undefined) {
      return false;
    }

    // Check if data has actually changed (including dateRange)
    const currentData = {
      scans: qrMetrics.scans,
      totalComplaints: complaintsData.totalComplaints,
      chartDataLength: filteredQuestion1Data?.length || 0,
      dateRange: dateRange
    };

    const hasDataChanged = 
      lastInsightDataRef.current.scans !== currentData.scans ||
      lastInsightDataRef.current.totalComplaints !== currentData.totalComplaints ||
      lastInsightDataRef.current.chartDataLength !== currentData.chartDataLength ||
      lastInsightDataRef.current.dateRange !== currentData.dateRange;

    if (hasDataChanged) {
      console.log('🧠 Data changed, should generate insights:', {
        old: lastInsightDataRef.current,
        new: currentData
      });
      lastInsightDataRef.current = currentData;
      return true;
    }

    return false;
  }, [metricsLoading, qrMetrics?.scans, complaintsData?.totalComplaints, filteredQuestion1Data?.length, dateRange]);

  useEffect(() => {
    if (shouldGenerateInsights) {
      console.log('🧠 Generating insights with new data for dateRange:', dateRange);
      generateInsights(qrMetrics, complaintsData, filteredQuestion1Data);
    }
  }, [shouldGenerateInsights]);

  const firstRowHeight = '40%';
  const lastRowHeight = '40%';

  // Error handling component
  if (hasError) {
    return (
      <div style={{ 
        height: '100%',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        flexDirection: 'column',
        gap: '16px',
        color: '#ef4444'
      }}>
        <div style={{ fontSize: '24px' }}>⚠️</div>
        <div style={{ fontSize: '16px', fontWeight: '600' }}>Error loading metrics</div>
        <div style={{ fontSize: '14px', color: '#6b7280', textAlign: 'center', maxWidth: '300px' }}>
          {errorMessage}
        </div>
        <button 
          onClick={refetchAll}
          style={{
            padding: '8px 16px',
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: 'white',
            border: '2px solid #e2e8f0',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: '500',
            fontSize: '14px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div style={{ 
        flex: 1, 
        overflow: 'auto', 
        display: 'flex', 
        flexDirection: 'column',
        width: '100%',
        gap: '16px',
        paddingBottom: '20px'
      }}>
        {/* Main feedback chart - Full width row */}
        <div style={{ 
          minHeight: '250px',
          width: '100%',
          flexShrink: 0
        }}>
          <ChartSection
            data={filteredQuestion1Data}
            title={question1Title}
            chartType="bar"
            isArchived={false}
            isLoading={isDataLoading}
            showInsights={true}
            insights={insights.length > 0 ? insights : [{ text: "Primary feedback metric", priority: "medium" }]}
            getOption={getOption}
          />
        </div>
        
        {/* QR Scan Display - Full width row */}
        <div style={{ 
          minHeight: '250px',
          width: '100%',
          flexShrink: 0
        }}>
          <QRScanDisplay 
            data={{
              scans: qrMetrics?.scans || 0,
              conversionRate: qrMetrics?.conversionRate || 0
            }}
            isLoading={qrMetrics?.isLoading || metricsLoading}
            error={qrMetrics?.error}
            insights={insights.filter(i => i.category === 'qr_performance')}
            recommendations={recommendations.filter(r => r.category === 'qr_optimization')}
            dateRange={dateRange}
          />
        </div>
        
        {/* Complaints Pie Chart - Full width row */}
        <div style={{ 
          minHeight: '250px',
          width: '100%',
          flexShrink: 0
        }}>
          <ComplaintsPieChart 
            data={complaintsData?.data || []}
            totalComplaints={complaintsData?.totalComplaints || 0}
            isLoading={complaintsData?.isLoading || metricsLoading}
            error={complaintsData?.error}
            insights={insights.filter(i => i.category === 'complaints')}
            recommendations={recommendations.filter(r => r.category === 'complaint_resolution')}
            dateRange={dateRange}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="site-content" style={{ 
      flex: 1, 
      overflow: 'hidden', 
      display: 'flex', 
      flexDirection: 'column',
      height: 'calc(100vh - 120px)',
      width: '100%'
    }}>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        gap: '24px',
        overflow: 'hidden',
        padding: '0 0 24px 0',
        width: '100%'
      }}>
        {/* Main chart section */}
        <div className="site-first-row" style={{ 
          display: 'flex', 
          gap: '24px', 
          height: firstRowHeight,
          minHeight: '200px',
          width: '100%',
          transition: 'height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
        }}>
          <div style={{ flex: 1, height: '100%' }}>
            <ChartSection
              data={filteredQuestion1Data}
              title={question1Title}
              chartType="bar"
              isArchived={false}
              isLoading={isDataLoading}
              showInsights={true}
              insights={insights.length > 0 ? insights : [{ text: "Primary feedback metric", priority: "medium" }]}
              getOption={getOption}
            />
          </div>
        </div>
        
        <div style={{ 
          height: lastRowHeight,
          minHeight: '250px',
          display: 'flex',
          gap: '24px',
          width: '100%'
        }}>
          <div style={{ 
            flex: 1, 
            height: '100%'
          }}>
            {console.log('🎯 Passing to QRScanDisplay:', {
              scans: qrMetrics?.scans,
              conversionRate: qrMetrics?.conversionRate,
              dateRange: dateRange,
              isLoading: qrMetrics?.isLoading || metricsLoading
            })}
            <QRScanDisplay 
              data={{
                scans: qrMetrics?.scans || 0,
                conversionRate: qrMetrics?.conversionRate || 0
              }}
              isLoading={qrMetrics?.isLoading || metricsLoading}
              error={qrMetrics?.error}
              insights={insights.filter(i => i.category === 'qr_performance')}
              recommendations={recommendations.filter(r => r.category === 'qr_optimization')}
              dateRange={dateRange} // CRITICAL: Make sure this is passed
            />
          </div>
          
          <div style={{ 
            flex: 1, 
            height: '100%'
          }}>
            {console.log('📊 Passing to ComplaintsPieChart:', {
              totalComplaints: complaintsData?.totalComplaints,
              dataLength: complaintsData?.data?.length,
              dateRange: dateRange,
              isLoading: complaintsData?.isLoading || metricsLoading
            })}
            <ComplaintsPieChart 
              data={complaintsData?.data || []}
              totalComplaints={complaintsData?.totalComplaints || 0}
              isLoading={complaintsData?.isLoading || metricsLoading}
              error={complaintsData?.error}
              insights={insights.filter(i => i.category === 'complaints')}
              recommendations={recommendations.filter(r => r.category === 'complaint_resolution')}
              dateRange={dateRange} // CRITICAL: Make sure this is passed
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DataView = () => {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState('site');
  const [showArchivedGraphs, setShowArchivedGraphs] = useState(false);
  const [hasPollingData, setHasPollingData] = useState(false);

  // Business selection state
  const [availableBusinesses, setAvailableBusinesses] = useState([]);
  const [selectedBusinessId, setSelectedBusinessId] = useState('');
  const [isBusinessesLoading, setIsBusinessesLoading] = useState(true);
  
  // Loading states - simplified since smart loading handles most of this
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasLoadingTimeout, setHasLoadingTimeout] = useState(false);

  // Site health score state
  const [siteHealthData, setSiteHealthData] = useState({
    score: 0,
    trends: { lastWeek: 0, lastMonth: 0 }
  });
  const [isHealthScoreLoading, setIsHealthScoreLoading] = useState(false);
  
  // Business view data state
  const [businessTimeSeriesData, setBusinessTimeSeriesData] = useState([]);
  const [isBusinessDataLoading, setIsBusinessDataLoading] = useState(false);
  const [businessState, setBusinessState] = useState({
    deviceIds: [],
    selectedDeviceId: '',
    loading: true,
    error: '',
    sumOfSites: 0,
    totalLines: 0,
    lastWeekLines: 0,
    token: '',
    business: '',
    industry: '',
    industryAverage: 0,
    data: []
  });

  const performanceTimers = useRef({
    componentMount: performance.now(),
    businessDataStart: null,
    storeDataStart: null,
    deckDataStart: null,
    chartDataStart: null
  });

  const forceCompleteRef = useRef(false);
  const emergencyTimeoutRef = useRef(null);
  const refreshAvailabilityRef = useRef(null);

  useEffect(() => {
    // Clear any existing timeout
    if (emergencyTimeoutRef.current) {
      clearTimeout(emergencyTimeoutRef.current);
    }
    
    // Set emergency timeout
    emergencyTimeoutRef.current = setTimeout(() => {
      if (!forceCompleteRef.current) {
        console.error('🚨 EMERGENCY: Forcing loading completion after 3 seconds');
        console.log('Current loading states:', {
          isSmartLoading,
          loadingStage,
          isDeckLoading,
          isChartDataLoading,
          isDeviceIdsLoading,
          isBusinessLoading: businessState.loading || isBusinessesLoading,
          isHealthScoreLoading,
          selectedStoreId,
          viewState
        });
        
        forceCompleteRef.current = true;
        setHasLoadingTimeout(true);
        
        // Force complete all loading states
        setIsInitialLoading(false);
        setIsBusinessesLoading(false);
        setIsHealthScoreLoading(false);
        setIsBusinessDataLoading(false);
        updateBusinessState({ loading: false });
      }
    }, 3000); // 3 second maximum
    
    return () => {
      if (emergencyTimeoutRef.current) {
        clearTimeout(emergencyTimeoutRef.current);
      }
    };
  }, []);
  

  const { businessId, businessName } = useBusinessData(USER);

  const {
    storeIds,
    selectedStoreId,
    setSelectedStoreId,
    sitePairings,
    isDeviceIdsLoading,
    connectionError: storeConnectionError,
    connectionErrorMessage: storeErrorMessage,
    storesWithData,
    updateStoresWithData,
    retryCount: storeRetryCount,
    isSmartLoading,
    loadingStage,
    sitesChecked,
    smartLoadingStrategy,
    rawStoresData
  } = useStoreData(selectedBusinessId || businessId, true, USER);

  // New: Device and Deck filter state
  const [selectedDeviceId, setSelectedDeviceId] = useState('all');
  const [selectedDeckId, setSelectedDeckId] = useState('all');

  // Fetch devices list for the selected business (all devices)
  const {
    devices: allDevices,
    deviceOptions: allDeviceOptions,
    deviceMapping: allDeviceMapping,
    isLoading: isDevicesLoading
  } = useDevicesList(selectedBusinessId || businessId);

  // Filter devices to only show those belonging to the selected store/site
  const { deviceOptions, deviceMapping } = useMemo(() => {
    if (!selectedStoreId || selectedStoreId === 'all' || !allDevices || allDevices.length === 0) {
      // If no store selected or devices not loaded, show all devices
      return {
        deviceOptions: allDeviceOptions,
        deviceMapping: allDeviceMapping
      };
    }

    // Filter devices by the selected store_id
    const filteredDevices = allDevices.filter(device => device.store_id === selectedStoreId);

    console.log('🔍 Filtered devices for store', selectedStoreId, ':', filteredDevices.length, 'of', allDevices.length);

    // Create filtered options and mapping
    const filteredOptions = filteredDevices.map(device => device.device_id);
    const filteredMapping = filteredDevices.reduce((acc, device) => {
      acc[device.device_id] = device.device_name || device.store_name || `Device ${device.device_id}`;
      return acc;
    }, {});

    return {
      deviceOptions: filteredOptions,
      deviceMapping: filteredMapping
    };
  }, [selectedStoreId, allDevices, allDeviceOptions, allDeviceMapping]);

  // Fetch decks list for the selected business
  const {
    decks: allDecksRaw,
    deckOptions: deckOptionsRaw,
    deckMapping: deckMappingRaw,
    isLoading: isDecksLoading
  } = useDecksList(selectedBusinessId || businessId, USER);

  // Filter decks to only include those for the current business
  const { allDecks, deckOptions, deckMapping } = useMemo(() => {
    const currentBusinessId = selectedBusinessId || businessId;

    // Filter decks that belong to the current business
    const filteredDecks = allDecksRaw.filter(deck =>
      deck.businessId === currentBusinessId ||
      deck.businessId === parseInt(currentBusinessId) ||
      deck.businessId?.toString() === currentBusinessId?.toString()
    );

    console.log('🎴 Filtered decks for business', currentBusinessId, ':', {
      total: allDecksRaw.length,
      filtered: filteredDecks.length,
      decks: filteredDecks.map(d => ({ id: d.deckId, name: d.deckName, businessId: d.businessId }))
    });

    // Create filtered options and mapping
    const filteredOptions = filteredDecks.map(deck => deck.deckId);
    const filteredMapping = filteredDecks.reduce((acc, deck) => {
      acc[deck.deckId] = deck.deckName || `Deck ${deck.deckId}`;
      return acc;
    }, {});

    return {
      allDecks: filteredDecks,
      deckOptions: filteredOptions,
      deckMapping: filteredMapping
    };
  }, [allDecksRaw, deckOptionsRaw, deckMappingRaw, selectedBusinessId, businessId]);

  // Filter sites based on selected deck
  const filteredStoreIds = useMemo(() => {
    if (selectedDeckId === 'all' || !allDecks || allDecks.length === 0) {
      // No deck filter, show all sites for the current business
      console.log('🏢 No deck filter - showing all business sites:', storeIds.length);
      return storeIds;
    }

    // Find the selected deck
    const selectedDeck = allDecks.find(deck => deck.deckId === selectedDeckId);
    if (!selectedDeck || !selectedDeck.storeIds) {
      console.warn('⚠️ Selected deck not found or has no stores:', selectedDeckId);
      return storeIds;
    }

    console.log('🔍 Filtering sites for deck:', {
      deckId: selectedDeckId,
      deckName: deckMapping[selectedDeckId],
      businessStores: storeIds,
      deckStores: selectedDeck.storeIds,
      selectedBusinessId: selectedBusinessId
    });

    // IMPORTANT: Only show stores that belong to BOTH:
    // 1. The current business (storeIds)
    // 2. The selected deck (selectedDeck.storeIds)
    const deckStoreIds = Array.isArray(selectedDeck.storeIds)
      ? selectedDeck.storeIds
      : (typeof selectedDeck.storeIds === 'string'
        ? selectedDeck.storeIds.split(',').map(id => id.trim())
        : []);

    const filtered = storeIds.filter(storeId => deckStoreIds.includes(storeId));

    console.log('🎯 Filtered sites for deck', selectedDeckId, ':', {
      filteredCount: filtered.length,
      totalBusinessStores: storeIds.length,
      totalDeckStores: deckStoreIds.length,
      filtered: filtered
    });

    return filtered;
  }, [selectedDeckId, allDecks, storeIds, selectedBusinessId, deckMapping]);

  // Create site options for dropdown - include "All Sites" only when "All Decks" is selected
  const siteOptionsForDropdown = useMemo(() => {
    if (selectedDeckId === 'all') {
      // When "All Decks" is selected, add "All Sites" option
      const options = ['all', ...filteredStoreIds];
      console.log('📋 Site options for ALL DECKS:', {
        selectedDeckId,
        filteredStoreIds: filteredStoreIds.length,
        options: options,
        sitePairings: Object.keys(sitePairings)
      });
      return options;
    }
    // When a specific deck is selected, show only sites for that deck
    console.log('📋 Site options for specific DECK:', {
      selectedDeckId,
      filteredStoreIds: filteredStoreIds.length,
      options: filteredStoreIds
    });
    return filteredStoreIds;
  }, [selectedDeckId, filteredStoreIds, sitePairings]);

  // Enhanced site pairings to include "All Sites"
  const enhancedSitePairings = useMemo(() => ({
    'all': 'All Sites',
    ...sitePairings
  }), [sitePairings]);

  // When deck selection changes, update the selected site if needed
  useEffect(() => {
    if (selectedDeckId === 'all') {
      // When switching to "All Decks", default to "All Sites"
      if (selectedStoreId !== 'all' && !filteredStoreIds.includes(selectedStoreId)) {
        console.log('🔄 Auto-selecting "All Sites" for "All Decks"');
        setSelectedStoreId('all');
      }
    } else if (filteredStoreIds.length > 0) {
      // When a specific deck is selected
      if (selectedStoreId === 'all' || !filteredStoreIds.includes(selectedStoreId)) {
        // "All Sites" is not valid for specific decks, or current site doesn't run this deck
        console.log('🔄 Auto-selecting first site for deck:', filteredStoreIds[0]);
        setSelectedStoreId(filteredStoreIds[0]);
      }
    }
  }, [selectedDeckId, filteredStoreIds, selectedStoreId, setSelectedStoreId]);

  // Build store-to-deck mapping for auto-toggle functionality
  const storeToDeckMap = useMemo(() => {
    const mapping = {};

    // Build reverse mapping: store -> deck
    // When a deck has storeIds, map each store to that deck
    allDecks.forEach(deck => {
      if (deck.storeIds) {
        const deckStoreIds = Array.isArray(deck.storeIds)
          ? deck.storeIds
          : (typeof deck.storeIds === 'string'
            ? deck.storeIds.split(',').map(id => id.trim())
            : []);

        deckStoreIds.forEach(storeId => {
          // Store the first deck found for this store
          // (if multiple decks per store, this picks the first)
          if (!mapping[storeId]) {
            mapping[storeId] = deck.deckId;
          }
        });
      }
    });

    console.log('🔄 Store-to-Deck mapping built:', mapping);
    return mapping;
  }, [allDecks]);

  // Reset device and deck filters when business changes
  useEffect(() => {
    setSelectedDeviceId('all');
    setSelectedDeckId('all');
  }, [selectedBusinessId]);

  // Smart event handler: When site changes, auto-toggle deck to match site's assigned deck
  const handleSiteChange = useCallback((newStoreId) => {
    console.log('🏪 Site changed to:', newStoreId);

    // 1. Update store selection
    setSelectedStoreId(newStoreId);

    // 2. Reset device when site changes (prevent invalid device selection)
    setSelectedDeviceId('all');

    // 3. Auto-toggle deck if site has a specific deck assigned
    if (newStoreId !== 'all') {
      const assignedDeckId = storeToDeckMap[newStoreId];
      if (assignedDeckId && assignedDeckId !== selectedDeckId) {
        console.log('🔄 Auto-toggling deck to:', assignedDeckId, 'for site:', newStoreId);
        setSelectedDeckId(assignedDeckId);
      }
    }
  }, [storeToDeckMap, selectedDeckId, selectedStoreId]);

  // Smart event handler: When deck changes, reset dependent filters
  const handleDeckChange = useCallback((newDeckId) => {
    console.log('🎯 Deck changed to:', newDeckId, {
      currentSelectedDeckId: selectedDeckId,
      currentSelectedStoreId: selectedStoreId,
      currentFilteredStoreIds: filteredStoreIds
    });

    // Update deck selection
    setSelectedDeckId(newDeckId);

    // Reset dependent filters
    setSelectedDeviceId('all');

    // If switching to "All Decks", reset site to "All Sites" as well
    if (newDeckId === 'all') {
      console.log('🔄 Switching to All Decks - resetting site to All Sites and showing all available sites');
      setSelectedStoreId('all');
      // The filtered store IDs will update based on selectedDeckId change
      console.log('📋 Expected available sites after reset:', storeIds.length);
    }
  }, [selectedDeckId, selectedStoreId, filteredStoreIds, storeIds]);

  // Smart event handler: When device changes, filter data by device
  // NOTE: Device acts as a filter on store-level data, not a separate hierarchy level
  // We NO LONGER change selectedStoreId/selectedDeckId to avoid unnecessary reloads
  const handleDeviceChange = useCallback((newDeviceId) => {
    console.log('📱 Device changed to:', newDeviceId, { allDevicesCount: allDevices?.length });

    if (newDeviceId === 'all') {
      console.log('  Selecting all devices');
      setSelectedDeviceId('all');
      return;
    }

    // Find the device in allDevices
    const deviceInfo = allDevices?.find(device => device.device_id === newDeviceId);
    console.log('  Device info found:', {
      deviceFound: !!deviceInfo,
      store_id: deviceInfo?.store_id,
      device_name: deviceInfo?.device_name
    });

    if (!deviceInfo?.store_id) {
      console.warn('  ⚠️ Device not found or has no store_id!');
      return;
    }

    // Only update the device selection
    // The charts will filter the current store/deck data by this device
    // This avoids triggering deck reloads and keeps the loading state clean
    console.log('🔄 Setting device filter to:', newDeviceId);
    setSelectedDeviceId(newDeviceId);

    // If user's currently selected store is different from device's store,
    // we still need to switch stores to show the device's store data
    if (selectedStoreId !== deviceInfo.store_id) {
      console.log('  Device is in different store, switching to:', deviceInfo.store_id);
      setSelectedStoreId(deviceInfo.store_id);

      // If the device's store has a different deck, switch to it
      const assignedDeckId = storeToDeckMap[deviceInfo.store_id];
      if (assignedDeckId && assignedDeckId !== selectedDeckId) {
        console.log('  Auto-toggling deck to:', assignedDeckId);
        setSelectedDeckId(assignedDeckId);
      }
    } else {
      console.log('  Device is in currently selected store, no store change needed');
    }
  }, [allDevices, storeToDeckMap, selectedStoreId, selectedDeckId]);



  // Determine which deck to use for data fetching
  // If user selected a specific deck, use that; otherwise use the site's current deck
  const effectiveDeckId = useMemo(() => {
    if (selectedDeckId === 'all') {
      // When "All Decks" is selected, we'll use the site's assigned deck
      // But we don't have it yet, so return null and it will be determined by useDeckData
      return null;
    }
    // Use the user-selected deck
    return selectedDeckId;
  }, [selectedDeckId]);

  const {
    currentDeck,
    isDataLoading: isDeckLoading,
    slideTitles: {
      question1Title
    },
    slideCount
  } = useDeckData(selectedStoreId, storeIds, updateStoresWithData, effectiveDeckId);

  // For data fetching, use the effective deck (fallback to currentDeck if using "All Decks")
  const deckIdForDataFetch = useMemo(() => {
    if (selectedDeckId === 'all') {
      // When "All Decks" is selected, use the site's assigned deck
      return currentDeck;
    }
    // Use the user-selected deck
    return selectedDeckId;
  }, [selectedDeckId, currentDeck]);

  // Log deck selection for debugging
  useEffect(() => {
    if (selectedDeckId !== 'all' && selectedDeckId !== deckIdForDataFetch) {
      console.log('🎯 Deck filter changed:', {
        selected: selectedDeckId,
        effective: deckIdForDataFetch,
        sitesCurrentDeck: currentDeck
      });
    }
  }, [selectedDeckId, deckIdForDataFetch, currentDeck]);

    // Handle initial loading state
  const {
    question1Data,
    question2Data,
    question3Data,
    question4Data,
    isDataLoading: isChartDataLoading,
    happyPeriodStart,
    happyPeriodEnd,
    unhappyPeriodStart,
    unhappyPeriodEnd,
    connectionError: chartConnectionError,
    connectionErrorMessage: chartErrorMessage,
    dateRange,
    setDateRange,
    chartType,
    setChartType,
    filteredData,
    filteredQuestion1Data,
    filteredQuestion2Data,
    filteredQuestion3Data,
    availableData
  } = useChartData(
    selectedStoreId,
    deckIdForDataFetch, // Use the deck ID for data fetching (currentDeck if "All Decks", user-selected otherwise)
    storeConnectionError,
    storeRetryCount,
    updateStoresWithData,
    true, // Enable smart loading
    selectedBusinessId || businessId, // Pass businessId for "All Sites" aggregation
    selectedDeviceId // Pass selectedDeviceId for device filtering
  );

  // Log chart data whenever it changes
  useEffect(() => {
    if (selectedDeviceId && selectedDeviceId !== 'all') {
      console.log('📊 Chart data after device filter:', {
        q1_filtered: filteredQuestion1Data?.length || 0,
        q2_filtered: filteredQuestion2Data?.length || 0,
        q3_filtered: filteredQuestion3Data?.length || 0,
        q4_filtered: filteredData?.length || 0,
        isLoading: isChartDataLoading,
        selectedDeviceId,
        selectedStoreId
      });
    }
  }, [filteredQuestion1Data, filteredQuestion2Data, filteredQuestion3Data, filteredData, isChartDataLoading, selectedDeviceId, selectedStoreId]);

  // Get site availability data (after dateRange is defined from useChartData)
  const {
    siteAvailability,
    isLoadingAvailability,
    refreshAvailability
  } = useSiteAvailability(storeIds, dateRange, selectedBusinessId || businessId);

  // Update the ref so handlers can access refreshAvailability
  useEffect(() => {
    refreshAvailabilityRef.current = refreshAvailability;
  }, [refreshAvailability]);

  // When device is selected, keep the current dateRange
  // (Don't auto-select because device-level data availability differs from store-level)
  useEffect(() => {
    if (selectedDeviceId && selectedDeviceId !== 'all') {
      console.log('📱 Device selected:', selectedDeviceId);
      console.log('  Current state:', {
        selectedStoreId,
        selectedDeckId,
        dateRange,
        isChartDataLoading,
        hasQuestion1Data: question1Data?.length > 0,
        hasQuestion4Data: question4Data?.length > 0
      });
      console.log('  Keeping current dateRange:', dateRange);
      // Charts will automatically filter by device_id at the data level
    }
  }, [selectedDeviceId, selectedStoreId, selectedDeckId, dateRange, isChartDataLoading, question1Data, question4Data]);

  // Auto-select the most recent date range with data when deck or site changes
  // NOTE: We intentionally do NOT include dateRange in dependencies to avoid
  // overriding user's manual date selections
  useEffect(() => {
    if (!selectedStoreId || !rawStoresData.length) return;

    // Skip auto-select if a specific device is selected (device effect handles this)
    if (selectedDeviceId && selectedDeviceId !== 'all') {
      console.log('📅 Specific device selected, skipping site-level date range auto-select');
      return;
    }

    // Skip auto-select for "All Sites" - default to 'all' (All Time)
    if (selectedStoreId === 'all') {
      console.log('📅 "All Sites" selected - defaulting to all time');
      setDateRange('all');
      return;
    }

    // Find the current store's data
    const currentStoreData = rawStoresData.find(store => store.store_id === selectedStoreId);
    if (!currentStoreData) return;

    // Date ranges in order of preference (most recent first)
    const dateRangePreference = [
      { range: 'today', field: 'has_data_today' },
      { range: 'yesterday', field: 'has_data_yesterday' },
      { range: 'week', field: 'has_data_this_week' },
      { range: 'month', field: 'has_data_this_month' },
      { range: 'year', field: 'has_data_this_year' }
    ];

    // Find the most recent date range with data
    for (const { range, field } of dateRangePreference) {
      if (currentStoreData[field]) {
        console.log('📅 Auto-selecting date range:', range, 'for site:', selectedStoreId);
        setDateRange(range);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeckId, selectedStoreId, rawStoresData]);
  // Check for polling data availability for all stores
  const {
    pollingAvailability,
    isLoading: isLoadingPollingAvailability
  } = usePollingAvailability(storeIds);

  useEffect(() => {
    // Only run when we have a selected store, raw data, and we're on 'today'
    if (selectedStoreId && rawStoresData.length > 0 && dateRange === 'today') {
      
      // Find the current store's data
      const currentStoreData = rawStoresData.find(store => store.store_id === selectedStoreId);
      
      if (currentStoreData) {
        console.log('🔍 Checking auto date range for store:', selectedStoreId, currentStoreData);
        
        // Check if current store has data today
        if (!currentStoreData.has_data_today) {
          console.log('🔄 No data today, looking for best date range...');
          
          // Find the best available date range in order of preference
          const dateRangeChecks = [
            { range: 'yesterday', field: 'has_data_yesterday' },
            { range: 'week', field: 'has_data_this_week' },
            { range: 'month', field: 'has_data_this_month' },
            { range: 'year', field: 'has_data_this_year' }
          ];
          
          for (const { range, field } of dateRangeChecks) {
            if (currentStoreData[field]) {
              console.log(`✅ Auto-switching to ${range} - found data!`);
              setDateRange(range);
              
              // Track this auto-switch
              posthog.capture('auto_date_range_switch', {
                from_range: 'today',
                to_range: range,
                site_id: selectedStoreId,
                reason: 'no_data_in_current_range',
                store_data: {
                  has_data_today: currentStoreData.has_data_today,
                  has_data_yesterday: currentStoreData.has_data_yesterday,
                  has_data_this_week: currentStoreData.has_data_this_week,
                  has_data_this_month: currentStoreData.has_data_this_month,
                  data_freshness: currentStoreData.data_freshness
                }
              });
              
              break; // Stop after finding first available range
            }
          }
          
          // If no data found in any range, log it
          if (!dateRangeChecks.some(check => currentStoreData[check.field])) {
            console.warn('⚠️ No data found in any date range for store:', selectedStoreId);
          }
        } else {
          console.log('✅ Store has data today, staying on today');
        }
      }
    }
  }, [selectedStoreId, rawStoresData, dateRange, setDateRange]);
  const enhancedStoresWithData = useMemo(() => {
    // Start with the existing data (from chart loading)
    const merged = { ...storesWithData };

    // Override with date-range-specific availability data
    Object.keys(siteAvailability).forEach(storeId => {
      merged[storeId] = siteAvailability[storeId];
    });

    // Also include polling data availability
    // A store is available if it has feedback data OR polling data
    Object.keys(pollingAvailability).forEach(storeId => {
      const hasFeedbackData = merged[storeId] !== false;
      const hasPollingData = pollingAvailability[storeId];

      // Mark as available (true) if either feedback or polling data exists
      merged[storeId] = hasFeedbackData || hasPollingData;
    });

    console.log('🔄 Enhanced stores with data (including polling):', merged);
    console.log('📊 Polling availability:', pollingAvailability);
    return merged;
  }, [storesWithData, siteAvailability, pollingAvailability]);

  // Build date range availability for dropdown
  // Maps each date range to whether the selected store(s) have data for it
  const dateRangeAvailability = useMemo(() => {
    // Handle "All Sites" case - aggregate availability across all stores
    if (selectedStoreId === 'all') {
      console.log('📅 "All Sites" selected - aggregating availability across', rawStoresData?.length || 0, 'stores');

      if (!rawStoresData || rawStoresData.length === 0) {
        return {
          today: false,
          yesterday: false,
          week: false,
          month: false,
          year: false,
          all: true
        };
      }

      // Check if ANY store has data for each date range
      const aggregatedAvailability = {
        today: rawStoresData.some(store => store.has_data_today) ? [true] : [],
        yesterday: rawStoresData.some(store => store.has_data_yesterday) ? [true] : [],
        week: rawStoresData.some(store => store.has_data_this_week) ? [true] : [],
        month: rawStoresData.some(store => store.has_data_this_month) ? [true] : [],
        year: rawStoresData.some(store => store.has_data_this_year) ? [true] : [],
        all: [true] // Always available
      };

      console.log('📅 "All Sites" availability (aggregated):', aggregatedAvailability);
      return aggregatedAvailability;
    }

    // Handle single store case
    const storeData = rawStoresData?.find(store => store.store_id === selectedStoreId);

    if (!storeData) {
      console.log('📅 Store data not found for date range availability');
      return {
        today: false,
        yesterday: false,
        week: false,
        month: false,
        year: false,
        all: true // Always available
      };
    }

    const availability = {
      today: storeData.has_data_today ? [storeData.feedback_count_today] : [],
      yesterday: storeData.has_data_yesterday ? [storeData.feedback_count_yesterday] : [],
      week: storeData.has_data_this_week ? [storeData.feedback_count_this_week] : [],
      month: storeData.has_data_this_month ? [storeData.feedback_count_this_month] : [],
      year: storeData.has_data_this_year ? [storeData.feedback_count_this_year] : [],
      all: [true] // Always available
    };

    console.log('📅 Date range availability for store', selectedStoreId, ':', availability);
    return availability;
  }, [selectedStoreId, rawStoresData]);

  // Add state to track if we have enough data for charts
  const [hasEnoughDataForCharts, setHasEnoughDataForCharts] = useState(false);

  const debugIntervalRef = useRef(null);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      debugIntervalRef.current = setInterval(() => {
        console.group('📋 DataView State Summary');
        // ... debug logging
        console.groupEnd();
      }, 10000); // Reduced frequency
      
      return () => {
        if (debugIntervalRef.current) {
          clearInterval(debugIntervalRef.current);
        }
      };
    }
  }, []);

  const shouldShowLoader = !forceCompleteRef.current && 
  (isSmartLoading || isDeckLoading || isChartDataLoading || 
   isDeviceIdsLoading || businessState.loading || isBusinessesLoading);

  useEffect(() => {
    const emergencyTimer = setTimeout(() => {
      console.error('🚨 DataView: Emergency timeout reached - something is stuck');
      setHasLoadingTimeout(true);
    }, 15000); // 15 second absolute maximum
    
    return () => clearTimeout(emergencyTimer);
  }, []);

  // Check for polling data when selectedStoreId changes
  useEffect(() => {
    const checkPollingData = async () => {
      if (!selectedStoreId) {
        setHasPollingData(false);
        return;
      }

      try {
        console.log(`🔍 Checking polling data for store: ${selectedStoreId}`);
        const response = await api.get(`/polls/store/${selectedStoreId}/analytics`);
        console.log('📊 Polling data response:', response.data);

        // Check if polls exist
        const hasPolls = response.data?.data?.polls && response.data.data.polls.length > 0;
        console.log(`✅ Has polling data: ${hasPolls}`);

        setHasPollingData(hasPolls);
      } catch (error) {
        console.warn('⚠️ Error checking polling data:', error.message);
        setHasPollingData(false);
      }
    };

    checkPollingData();
  }, [selectedStoreId]);

  const forceShowContent = hasLoadingTimeout || !shouldShowLoader;
  // Handle when everything is complete
  const handleLoadingComplete = () => {
    console.log('🎉 All loading complete - WordCloud will fade out');
    setIsInitialLoading(false);
  };

  const siteHealthCacheRef = useRef(new Map());

  // Data fetching functions wrapped in useCallback
  const fetchSiteHealthData = useCallback(async () => {
    if (!selectedStoreId || viewState === 'business') return;
    
    // Check cache first
    const cacheKey = `${selectedStoreId}-${viewState}`;
    const cachedData = siteHealthCacheRef.current.get(cacheKey);
    const cacheAge = cachedData ? Date.now() - cachedData.timestamp : Infinity;
    
    // Use cached data if less than 5 minutes old
    if (cachedData && cacheAge < 5 * 60 * 1000) {
      console.log('🚀 Using cached site health data');
      setSiteHealthData(cachedData.data);
      return;
    }
    
    try {
      setIsHealthScoreLoading(true);
      
      // Use Promise.allSettled instead of Promise.all to handle partial failures
      const responses = await Promise.allSettled([
        api.get(`/analytics/v2/question_1?site_id=${selectedStoreId}`),
        api.get(`/analytics/v2/question_2?site_id=${selectedStoreId}`),
        api.get(`/analytics/v2/question_3?site_id=${selectedStoreId}`)
      ]);
      
      const allFeedbackData = responses
        .filter(result => result.status === 'fulfilled')
        .flatMap(result => result.value.data.results || []);
      
      const scoreData = calculateSiteHealthScore(allFeedbackData);
      
      // Cache the result
      siteHealthCacheRef.current.set(cacheKey, {
        data: scoreData,
        timestamp: Date.now()
      });
      
      setSiteHealthData(scoreData);
    } catch (error) {
      console.error('Error fetching site health data:', error);
    } finally {
      setIsHealthScoreLoading(false);
    }
  }, [selectedStoreId, viewState]);

  const fetchBusinessTimeSeriesData = useCallback(async () => {
    if (!businessState.token) return;
    
    try {
      setIsBusinessDataLoading(true);
      console.log('Fetching business time series data for token:', businessState.token);
      
      const businessViewData = await fetchBusinessViewDataByToken(businessState.token);
      console.log('Raw business view data:', businessViewData);

      const transformedData = transformBusinessDataForChart(businessViewData);
      console.log('Transformed data for chart:', transformedData);

      setBusinessTimeSeriesData(transformedData);

    
      if (transformedData && transformedData.length > 0) {
        // Check if we have enough data points for meaningful charts
        const uniqueWeeks = new Set(transformedData.map(entry => entry.week_starting));
        const hasEnoughData = uniqueWeeks.size >= 2;

        setHasEnoughDataForCharts(hasEnoughData);
      } else {
        console.log('No business view data available');
        setHasEnoughDataForCharts(false);
      }
    } catch (error) {
      console.error('Error fetching business time series data:', error);
      setHasEnoughDataForCharts(false);
    } finally {
      setIsBusinessDataLoading(false);
    }
  }, [businessState.token]);

  const transformBusinessDataForChart = (rawData) => {
    if (!rawData || !Array.isArray(rawData)) {
      console.warn('Invalid business data for transformation:', rawData);
      return [];
    }

    console.log('🔄 Transforming business data for chart...');
    
    // Group data by week_starting date
    const groupedByWeek = rawData.reduce((acc, item) => {
      const weekKey = item.week_starting;
      if (!acc[weekKey]) {
        acc[weekKey] = {
          week_starting: weekKey,
          date: weekKey, // Chart might expect 'date' property
          sites: {}
        };
      }
      
      const siteName = item.site || 'Unknown Site';
      acc[weekKey].sites[siteName] = {
        feedback_score: item.fdbk_score || 0,
        feedback_count: item.sum_of_feedback_week || 0,
        feedback_last_week: item.sum_of_feedback_last_week || 0,
        score_vs_previous: item.fdbk_score_prev || 0
      };
      
      return acc;
    }, {});

    // Convert to chart-friendly format
    const chartData = Object.values(groupedByWeek).map(weekData => {
      const dataPoint = {
        date: weekData.week_starting,
        week_starting: weekData.week_starting,
      };

      // Add each site's data as separate properties
      Object.entries(weekData.sites).forEach(([siteName, siteData]) => {
        // Clean site name for property names (remove spaces, special chars)
        const cleanSiteName = siteName.replace(/[^a-zA-Z0-9]/g, '_');
        
        dataPoint[siteName] = siteData.feedback_score; // Main value for line chart
        dataPoint[`${cleanSiteName}_score`] = siteData.feedback_score;
        dataPoint[`${cleanSiteName}_count`] = siteData.feedback_count;
        dataPoint[`${cleanSiteName}_vs_prev`] = siteData.score_vs_previous;
      });

      return dataPoint;
    });

    // Sort by date
    const sortedData = chartData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log('✅ Transformed chart data:', sortedData);
    console.log('📊 Sample data point:', sortedData[0]);
    
    return sortedData;
  };

    // Function to update business state partially
  const updateBusinessState = useCallback((updates) => {
    setBusinessState(prev => ({ ...prev, ...updates }));
  }, []);

  // Handle business selection change
  const handleBusinessChange = useCallback((businessId) => {
    console.log('handleBusinessChange called with:', businessId, 'Current selectedBusinessId:', selectedBusinessId);  
    if (businessId !== selectedBusinessId) {
      console.log('🔄 Switching to business:', businessId);
      
      // Analytics tracking
      posthog.capture('business_switched', {
        from_business: selectedBusinessId,
        to_business: businessId,
        user_type: 'dashboard_user'
      });
      
      // Find the selected business
      const selectedBusiness = availableBusinesses.find(b => b.business_id === businessId);
      console.log('Selected business details:', selectedBusiness);
      // CLEAR ALL PREVIOUS DATA FIRST
      setSiteHealthData({
        score: 0,
        trends: { lastWeek: 0, lastMonth: 0 }
      });
      
      // Clear any cached site health data
      siteHealthCacheRef.current.clear();
      
      // Batch all state updates
      setSelectedBusinessId(businessId);
      setSelectedStoreId(''); // This is crucial - clear the store selection
      setSelectedBusinessIdToStorage(businessId);
      
      // Update business state and clear previous business data
      if (selectedBusiness) {
        updateBusinessState({
          token: businessId,
          business: selectedBusiness.business_name || '',
          loading: true,
          // Reset ALL business-specific data to prevent stale data
          deviceIds: [],
          data: [],
          sumOfSites: 0,
          totalLines: 0,
          lastWeekLines: 0,
          error: ''
        });
      }
      
      // Clear business time series data
      setBusinessTimeSeriesData([]);
      setHasEnoughDataForCharts(false);
    }
  }, [selectedBusinessId, availableBusinesses, setSelectedStoreId, updateBusinessState]);
  
  // Helper to generate chart options
  const getOption = useCallback((title, data, type, peaks) => {
    return getChartOptions(
      title, 
      data, 
      type, 
      peaks, 
      dateRange,  // Make sure this is passed
      happyPeriodStart, 
      happyPeriodEnd, 
      unhappyPeriodStart, 
      unhappyPeriodEnd
    );
  }, [dateRange, happyPeriodStart, happyPeriodEnd, unhappyPeriodStart, unhappyPeriodEnd]);

  const isDataLoading = isDeckLoading || isChartDataLoading;
  
  // Destructure business state for convenience
  const {
    loading,
    error,
    sumOfSites,
    totalLines,
    lastWeekLines,
    token,
    business,
    data
  } = businessState;

  // Function to handle view toggle with proper data loading
  const handleViewToggle = useCallback((newViewState) => {
    console.log('handleViewToggle called with:', newViewState);
    console.log('Current viewState:', viewState);

    posthog.capture('dashboard_view_changed', {
      previous_view: viewState,
      new_view: newViewState,
      site_id: selectedStoreId,
      business_id: selectedBusinessId,
      user_type: 'dashboard_user'
    });
    
    if (newViewState !== viewState) {
      setViewState(newViewState);
      
      setTimeout(() => {
        if (newViewState === 'business' && token) {
          console.log('Fetching business data...');
          fetchBusinessTimeSeriesData();
        } else if ((newViewState === 'site' || newViewState === 'response' ) && selectedStoreId) {
          console.log('Fetching site health data...');
          fetchSiteHealthData();
        }
      }, 0);
    }
  }, [viewState, selectedStoreId, selectedBusinessId, token, fetchBusinessTimeSeriesData, fetchSiteHealthData]);

  useEffect(() => {
    // Start fetching data when we have a selected store and smart loading has progressed
    if (loadingStage !== 'scanning' && selectedStoreId && (viewState === 'site' || viewState === 'response')) {
      console.log('🔄 Fetching site health data...');
      fetchSiteHealthData();
    }
  }, [selectedStoreId, viewState, loadingStage, fetchSiteHealthData]);

  useEffect(() => {
    // Start fetching business data when we have a token and smart loading has progressed
    if (loadingStage !== 'scanning' && viewState === 'business' && businessState.token) {
      console.log('🔄 Fetching business time series data...');
      fetchBusinessTimeSeriesData();
    }
  }, [viewState, businessState.token, loadingStage, fetchBusinessTimeSeriesData]);

  // Handle fetching businesses and setting up initial state
  useEffect(() => {
    const fetchInitialData = async () => {
      performanceTimers.current.businessDataStart = performance.now();
      console.log('⏱️ Starting business data fetch...');
      try {
        setIsBusinessesLoading(true);
        const businesses = await fetchBusinessByEmail(USER);
        console.log(`✅ Business data loaded in ${performance.now() - performanceTimers.current.businessDataStart}ms`);
        
        if (businesses && businesses.length > 0) {
          setAvailableBusinesses(businesses);
          const savedBusinessId = getSelectedBusinessIdFromStorage();
          let defaultBusinessId = '';
          let defaultBusiness = null;
          
          console.log('Saved business ID from localStorage:', savedBusinessId);
          console.log('Available businesses:', businesses.map(b => ({ id: b.business_id, name: b.business_name })));
          
          if (savedBusinessId) {
            const savedBusinessExists = businesses.find(b => 
              b.business_id === savedBusinessId || 
              b.business_id === parseInt(savedBusinessId) || 
              b.business_id.toString() === savedBusinessId
            );
            
            if (savedBusinessExists) {
              defaultBusinessId = savedBusinessExists.business_id;
              defaultBusiness = savedBusinessExists;
              console.log('✅ Using saved business ID from localStorage:', savedBusinessId, 'matched business:', defaultBusiness.business_name);
            } else {
              console.log('❌ Saved business ID not found in available businesses, using first business');
              defaultBusinessId = businesses[0].business_id;
              defaultBusiness = businesses[0];
            }
          } else {
            // No saved business ID, use the first one
            defaultBusinessId = businesses[0].business_id;
            defaultBusiness = businesses[0];
            console.log('🆕 No saved business ID, using first business:', defaultBusinessId);
          }
          
          setSelectedBusinessId(defaultBusiness.business_id);
          
          const savedIdString = savedBusinessId ? savedBusinessId.toString() : '';
          const currentIdString = defaultBusiness.business_id.toString();
          
          if (savedIdString !== currentIdString) {
            console.log('💾 Updating localStorage: saved =', savedIdString, 'current =', currentIdString);
            setSelectedBusinessIdToStorage(defaultBusiness.business_id.toString());
          } else {
            console.log('✨ localStorage already has correct value, no update needed');
          }
          
          updateBusinessState({ 
            token: defaultBusiness.business_id,
            business: defaultBusiness.business_name || ''
          });
        }
      } catch (error) {
        console.error('Error during initial data fetch:', error);
        console.error(`❌ Business data failed after ${performance.now() - performanceTimers.current.businessDataStart}ms:`, error);
        updateBusinessState({ 
          error: 'Failed to load initial data',
          loading: false
        });
      } finally {
        setIsBusinessesLoading(false);
      }
    };
    
    if (USER) {
      fetchInitialData();
    }
  }, [updateBusinessState]);

  useEffect(() => {
  const logInterval = setInterval(() => {
    const elapsed = performance.now() - performanceTimers.current.componentMount;
    console.log(`📊 Loading status at ${elapsed.toFixed(0)}ms:`, {
      elapsed,
      isSmartLoading,
      loadingStage,
      isDeckLoading,
      isChartDataLoading,
      isDeviceIdsLoading,
      isBusinessLoading: businessState.loading || isBusinessesLoading,
      selectedStoreId: selectedStoreId || 'none',
      shouldShowLoader
    });
  }, 2000); // Log every 2 seconds
  
  return () => clearInterval(logInterval);
}, [isSmartLoading, loadingStage, isDeckLoading, isChartDataLoading, 
    isDeviceIdsLoading, businessState.loading, isBusinessesLoading, 
    selectedStoreId, shouldShowLoader]);

  // Process business data when selectedBusinessId changes
  useEffect(() => {
    if (!selectedBusinessId) return;
    
    const fetchBusinessData = async () => {
      try {
        updateBusinessState({ loading: true });
        
        const [
          storesResponse, 
          linesThisWeek, 
          linesLastWeek, 
          industryResponse,
          businessViewData
        ] = await Promise.all([
          fetchStoresByBusinessId(selectedBusinessId),
          fetchLinesByToken(selectedBusinessId),
          fetchLinesLastWeek(selectedBusinessId),
          getIndustryByToken(selectedBusinessId),
          fetchBusinessViewDataByToken(selectedBusinessId)
        ]);
        
        const storesData = Array.isArray(storesResponse) ? storesResponse : storesResponse?.data;
        if (!storesData || !Array.isArray(storesData)) {
          throw new Error('Invalid stores data format');
        }
        
        const ids = storesData.map(row => row.store_id);
        
        const reshapedData = businessViewData.map((entry) => ({
          site: entry.site,
          feedbackThisWeek: entry.sum_of_feedback_week,
          feedbackLastWeek: entry.sum_of_feedback_last_week || 0,
          feedbackScore: entry.fdbk_score,
          vsLastWeek: entry.fdbk_score_prev
        }));
        
        updateBusinessState({
          deviceIds: ids,
          selectedDeviceId: ids.length > 0 ? ids[0] : '',
          sumOfSites: ids.length,
          totalLines: linesThisWeek,
          lastWeekLines: linesLastWeek,
          data: reshapedData,
          loading: false,
          error: ''
        });
      } catch (error) {
        console.error('Error fetching business data', error);
        updateBusinessState({
          loading: false,
          error: 'Failed to load business data'
        });
      }
    };
    
    fetchBusinessData();
  }, [selectedBusinessId, updateBusinessState]);

  // Log chart data state for debugging
  useEffect(() => {
    console.log('📊 Chart Data Render State:', {
      isDataLoading,
      isChartDataLoading,
      dateRange,
      filteredQuestion1DataLength: filteredQuestion1Data?.length || 0,
      filteredDataLength: filteredData?.length || 0,
      selectedStoreId,
      selectedDeviceId
    });
  }, [isDataLoading, isChartDataLoading, dateRange, filteredQuestion1Data?.length, filteredData?.length, selectedStoreId, selectedDeviceId]);

  // Debug: Log when selectedStoreId or selectedDeviceId changes
  useEffect(() => {
    console.log('🔍 Selection changed:', {
      selectedStoreId,
      selectedDeviceId,
      storesCount: storeIds?.length
    });
  }, [selectedStoreId, selectedDeviceId, storeIds?.length]);

  return (
    <LoadingProvider>
      <div className='main-content' style={{ position: 'relative' }}>
        <div className='high'>
          <Header />
        </div>
    
        {/* Controls */}
        <div className="controls-row">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {availableBusinesses.length > 1 && (
            <UnifiedDropdown
              type="business"
              selectedValue={selectedBusinessId}
              setSelectedValue={handleBusinessChange}
              options={availableBusinesses.map(b => b.business_id)}
              displayMapping={availableBusinesses.reduce((acc, b) => ({ 
                ...acc, 
                [b.business_id]: b.business_name 
              }), {})}
            />
            )}
            
            <div className="view-toggle-container">
              <ViewToggle 
                viewState={viewState} 
                setViewState={handleViewToggle} 
              />
            </div>
          </div>

          {selectedStoreId && (viewState === 'site' || viewState === 'response') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div
                style={{ position: 'relative' }}
                className="polling-button-wrapper"
              >
                <Button
                  variant="secondary"
                  size="large"
                  icon={<BarChart3 className="w-4 h-4 mr-2" />}
                  iconPosition="left"
                  disabled={!hasPollingData}
                  onClick={() => {
                    if (!hasPollingData) return;
                    posthog.capture('polling_data_view_clicked', {
                      site_id: selectedStoreId,
                      business_id: selectedBusinessId,
                      view_state: viewState
                    });
                    navigate(`/polling-data/${selectedStoreId}`);
                  }}
                >
                  Polling Data
                </Button>
                {!hasPollingData && (
                  <div className="polling-tooltip">
                    No polling data found for this site
                  </div>
                )}
              </div>
            </div>
          )}
          
          {(viewState === 'site' || viewState === 'response') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <ChartControls
                isDeviceIdsLoading={isDeviceIdsLoading}
                selectedStoreId={selectedStoreId}
                setSelectedStoreId={setSelectedStoreId}
                storeIds={siteOptionsForDropdown}
                sitePairings={enhancedSitePairings}
                storesWithData={enhancedStoresWithData}
                dateRange={dateRange}
                setDateRange={setDateRange}
                availableData={dateRangeAvailability}
                isLoadingAvailability={isLoadingAvailability}
                refreshAvailability={refreshAvailability}
                selectedDeviceId={selectedDeviceId}
                setSelectedDeviceId={setSelectedDeviceId}
                deviceOptions={deviceOptions}
                deviceMapping={deviceMapping}
                isDevicesLoading={isDevicesLoading}
                selectedDeckId={selectedDeckId}
                setSelectedDeckId={setSelectedDeckId}
                deckOptions={deckOptions}
                deckMapping={deckMapping}
                isDecksLoading={isDecksLoading}
                // Smart handlers for preventing circular dependencies
                onSiteChange={handleSiteChange}
                onDeckChange={handleDeckChange}
                onDeviceChange={handleDeviceChange}
              />
            </div>
          )}
          {(viewState === 'business') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>

            <Button
              variant="primary"
              size="large"
              icon={<ArrowRight className="w-4 h-4 ml-2" />}
              iconPosition="right"
              onClick={() => navigate('/reporting')}
            >
              Reporting
            </Button>
          </div>
          )}
        </div>
    
        <div style={{ 
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          paddingLeft: '0',
          paddingRight: '0',
          margin: '0',
          filter: (shouldShowLoader && !hasLoadingTimeout) ? 'blur(1px)' : 'none',
          opacity: (shouldShowLoader && !hasLoadingTimeout) ? 0.9 : 1,
          transition: 'filter 0.8s ease-out, opacity 0.5s ease-out'
        }}>
          {viewState === 'business' ? (
            <BusinessViewContent 
              sumOfSites={sumOfSites}
              totalLines={totalLines}
              lastWeekLines={lastWeekLines}
              businessTimeSeriesData={businessTimeSeriesData}
              isBusinessDataLoading={isBusinessDataLoading}
              hasEnoughDataForCharts={hasEnoughDataForCharts}
              getOption={getOption}
              selectedBusinessId={selectedBusinessId}
            />
          ) : viewState === 'site' ? (
            <SiteViewContent
              selectedStoreId={selectedStoreId}
              filteredQuestion1Data={filteredQuestion1Data}
              question1Title={question1Title}
              isDataLoading={isDataLoading}
              filteredData={filteredData}
              getOption={getOption}
              dateRange={dateRange}
              selectedBusinessId={selectedBusinessId}
              businessId={businessId}
            />
          ) : viewState === 'response' ? (
            <ResponseViewContent 
              slideCount={slideCount}
              showArchivedGraphs={showArchivedGraphs}
              filteredQuestion1Data={filteredQuestion1Data}
              question1Title={question1Title}
              isDataLoading={isDataLoading}
              filteredData={filteredData}
              getOption={getOption}
              selectedStoreId={selectedStoreId}
              businessId={selectedBusinessId}
              dateRange={dateRange} // Already added in previous update
            />
          ) : null}
        </div>
        {shouldShowLoader && !hasLoadingTimeout && (
          <SmartWordCloudLoader
            isSmartLoading={isSmartLoading}
            loadingStage={loadingStage}
            onComplete={handleLoadingComplete}
          />
        )}
      </div>
    </LoadingProvider>
  );
};

export default DataView;